// frontend/src/store/authStore.js
// Store de Zustand para gestionar el estado de autenticación del usuario.

import { create } from 'zustand';

const getStoredToken = () => {
  try {
    return sessionStorage.getItem('token');
  } catch (error) {
    return null;
  }
};

const getStoredUser = () => {
  try {
    const userJson = sessionStorage.getItem('user');
    return userJson ? JSON.parse(userJson) : null;
  } catch (error) {
    return null;
  }
};

const getStoredAccount = () => {
  try {
    const accountJson = sessionStorage.getItem('account');
    return accountJson ? JSON.parse(accountJson) : null;
  } catch (error) {
    return null;
  }
};

/**
 * @typedef {object} AuthState
 * @property {string|null} token - Token JWT del usuario autenticado.
 * @property {object|null} user - Objeto con la información del usuario (id, username, role).
 * @property {boolean} isAuthenticated - Verdadero si el usuario está autenticado.
 * @property {boolean} isInitializing - Verdadero mientras se hidrata la sesión Entra.
 * @property {boolean} accessProvisioningRequired - Verdadero si el usuario autenticado aún no tiene acceso local.
 * @property {function(string, object, object|null)} setAuthData - Función para establecer el token y los datos del usuario.
 * @property {function()} clearAuthData - Función para limpiar la sesión local.
 * @property {function(boolean)} setIsInitializing - Función para marcar el estado de inicialización.
 */

/**
 * @type {import('zustand').UseBoundStore<AuthState>}
 */
export const useAuthStore = create((set) => ({
  token: getStoredToken(),
  user: getStoredUser(),
  account: getStoredAccount(),
  isAuthenticated: !!getStoredToken(),
  isInitializing: true,
  accessProvisioningRequired: false,

  /**
   * @function setAuthData
   * @description Almacena el token JWT y los datos del usuario, y actualiza el estado de autenticación.
   * @param {string} token - El token JWT.
   * @param {object} userData - La información del usuario.
   */
  setAuthData: (token, userData, account = null) => {
    try {
      sessionStorage.setItem('token', token);
      sessionStorage.setItem('user', JSON.stringify(userData));
      if (account) {
        sessionStorage.setItem('account', JSON.stringify(account));
      }
    } catch (error) {
      // Si el navegador bloquea storage, el estado sigue vivo en memoria.
    }
    set({ token, user: userData, account, isAuthenticated: !!token, isInitializing: false, accessProvisioningRequired: false });
  },

  clearAuthData: () => {
    try {
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('account');
    } catch (error) {
      // Ignorar errores de storage.
    }
    set({ token: null, user: null, account: null, isAuthenticated: false, isInitializing: false, accessProvisioningRequired: false });
  },

  setIsInitializing: (isInitializing) => {
    set({ isInitializing });
  },

  setAccessProvisioningRequired: (accessProvisioningRequired) => {
    set({ accessProvisioningRequired });
  },
}));
